/* =========================================================================
   Prayer Break — "A Moment of Quiet" (PLAN-gameplay-v2 §3a, Eric's mockup).

   A quiet, deliberately NON-GAMIFIED pause the Moment Director grants after
   ~30 answered questions: small-caps title → praying-Theo art → one large
   prayer line → muted attribution → a countdown ring → "Amen. Continue".

   REVERENCE POLICY (absolute, mirrors db/042's header): no coins, no XP, no
   streak credit, no sounds of the game. The ring is ENCOURAGEMENT, never a
   lock — "Amen. Continue" is enabled from the first frame, so a player who
   wants to move on always can. prefers-reduced-motion drops the animation and
   simply shows the full ring.

   Content: authed players get the server's per-user least-seen rotation
   (GET /api/game/prayer-break, all 20 rows of db/042). Guests, offline players
   and any failure fall back to the SIX rows bundled verbatim below, cycled
   least-recently-shown through localStorage — so the moment always lands.

   House pattern: pure logic (payload normalization + the fallback rotation) is
   exported for tests; the DOM lives in showPrayerBreak(), built on demand like
   roundPicker.ts so nothing is left in the shell.
   ========================================================================= */
import { getPrayerBreak, isAuthenticated } from './api';
import type { KVStore } from './dailyBlessing';
import { currentIdentity } from './resumeGame';
import { withTimeout } from './serverGame';
import { prefersReducedMotion } from './ui/momentOverlay';

/** One moment of quiet — the shape of both the server row and the fallbacks. */
export interface PrayerBreakContent {
  code: string;
  title: string;
  line: string;
  attribution: string;
  fullText: string;
  holdSeconds: number;
}

/** Sane bounds for the countdown ring (db/042 seeds 20–30s). */
export const MIN_HOLD_SECONDS = 10;
export const MAX_HOLD_SECONDS = 60;
export const DEFAULT_HOLD_SECONDS = 25;

/** A slow server must never hold a quiet moment hostage — fall back instead. */
const PRAYER_FETCH_TIMEOUT_MS = 2_000;

/* ------------------------------ bundled fallback ------------------------------ */

/** SIX rows copied VERBATIM from production/db/042_prayer_breaks.sql (pb-001,
    pb-003, pb-006, pb-008, pb-014, pb-019). The server owns the full 20-row
    rotation; these keep guests and offline players in the same voice. */
export const FALLBACK_PRAYERS: readonly PrayerBreakContent[] = [
  {
    code: 'pb-001',
    title: 'A Moment of Quiet',
    line: 'Lord, make me an instrument of your peace.',
    attribution: 'Prayer of St. Francis',
    fullText:
      'Where there is hatred, let me sow love; where there is injury, pardon; where there is doubt, faith; where there is despair, hope.',
    holdSeconds: 25,
  },
  {
    code: 'pb-003',
    title: 'Be Still',
    line: 'Be still, and know that I am God.',
    attribution: 'Psalm 46',
    fullText: '',
    holdSeconds: 25,
  },
  {
    code: 'pb-006',
    title: 'Rest a Moment',
    line: 'The Lord is my shepherd; there is nothing I shall want.',
    attribution: 'Psalm 23',
    fullText:
      'He makes me lie down in green pastures; he leads me beside still waters; he restores my soul.',
    holdSeconds: 25,
  },
  {
    code: 'pb-008',
    title: 'A Moment of Quiet',
    line: 'My Lord and my God.',
    attribution: 'St. Thomas the Apostle · John 20:28',
    fullText: '',
    holdSeconds: 20,
  },
  {
    code: 'pb-014',
    title: 'Be Still',
    line: 'Let nothing disturb you. Let nothing frighten you. God never changes.',
    attribution: 'St. Teresa of Ávila',
    fullText: 'Patience obtains all things. Whoever has God lacks nothing. God alone suffices.',
    holdSeconds: 27,
  },
  {
    code: 'pb-019',
    title: 'Come, Lord Jesus',
    line: 'Maranatha — Come, Lord Jesus.',
    attribution: 'Revelation 22:20',
    fullText: '',
    holdSeconds: 20,
  },
];

export const PRAYER_SEEN_KEY_PREFIX = 'catholics.prayerSeen:';

/** Per-identity key, like the Director's counters. PURE. */
export function prayerSeenKeyFor(identity: string): string {
  return PRAYER_SEEN_KEY_PREFIX + identity;
}

/**
 * The next fallback code: the first never-shown row in catalog order, else the
 * LEAST-RECENTLY-shown one. `recent` is oldest-first. Mirrors the discipline of
 * db/042's usp_PrayerBreak_Next (least-seen, then least-recently-seen). PURE.
 */
export function pickFallbackCode(codes: readonly string[], recent: readonly string[]): string {
  if (codes.length === 0) return '';
  let best = codes[0];
  let bestRank = Number.POSITIVE_INFINITY;
  for (const code of codes) {
    const idx = recent.lastIndexOf(code);
    const rank = idx < 0 ? -1 : idx; // never shown sorts ahead of everything
    if (rank < bestRank) {
      bestRank = rank;
      best = code;
      if (rank === -1) break; // catalog order breaks ties among the unseen
    }
  }
  return best;
}

/** `recent` after showing `code` — de-duplicated, oldest-first, capped. PURE. */
export function noteShown(code: string, recent: readonly string[], cap: number): string[] {
  const next = recent.filter((c) => c !== code);
  next.push(code);
  while (next.length > cap) next.shift();
  return next;
}

/** Read the seen-list; anything off-shape restarts the rotation. PURE. */
export function parseSeen(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const p = JSON.parse(raw) as unknown;
    if (!Array.isArray(p)) return [];
    return p.filter((c): c is string => typeof c === 'string');
  } catch {
    return [];
  }
}

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Pick + record the next bundled prayer for this identity. */
export function nextFallbackPrayer(
  store: KVStore | null = deviceStore(),
  identity: string = safeIdentity(),
): PrayerBreakContent {
  const codes = FALLBACK_PRAYERS.map((p) => p.code);
  const key = prayerSeenKeyFor(identity);
  let recent: string[] = [];
  try {
    recent = parseSeen(store ? store.getItem(key) : null);
  } catch {
    recent = [];
  }
  const code = pickFallbackCode(codes, recent);
  try {
    store?.setItem(key, JSON.stringify(noteShown(code, recent, codes.length)));
  } catch {
    /* private mode — the rotation restarts next boot, which is harmless */
  }
  return FALLBACK_PRAYERS.find((p) => p.code === code) ?? FALLBACK_PRAYERS[0];
}

function safeIdentity(): string {
  try {
    return currentIdentity();
  } catch {
    return 'guest:unknown';
  }
}

/* ------------------------------ server payload ------------------------------ */

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Defensive read of GET /api/game/prayer-break. A row without a display LINE is
 * not a moment of quiet, so it collapses to null and the caller falls back.
 * PURE.
 */
export function normalizePrayerBreak(raw: unknown): PrayerBreakContent | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const line = str(r.line);
  if (!line) return null;
  const hold = typeof r.holdSeconds === 'number' && Number.isFinite(r.holdSeconds) ? Math.round(r.holdSeconds) : NaN;
  return {
    code: str(r.code) || 'pb-server',
    title: str(r.title) || 'A Moment of Quiet',
    line,
    attribution: str(r.attribution),
    fullText: str(r.fullText),
    holdSeconds: Number.isFinite(hold)
      ? Math.min(MAX_HOLD_SECONDS, Math.max(MIN_HOLD_SECONDS, hold))
      : DEFAULT_HOLD_SECONDS,
  };
}

/** The content for this moment: server rotation when authed and reachable,
    bundled rotation otherwise. Never throws, never blocks past the timeout. */
export async function loadPrayerBreak(): Promise<PrayerBreakContent> {
  if (isAuthenticated()) {
    const r = await withTimeout(getPrayerBreak(), PRAYER_FETCH_TIMEOUT_MS, {
      ok: false as const,
      offline: true,
      error: 'timeout',
    });
    if (r.ok) {
      const c = normalizePrayerBreak(r.data);
      if (c) return c;
    }
  }
  return nextFallbackPrayer();
}

/* --------------------------------- the screen --------------------------------- */

const OVERLAY_ID = 'prayerBreakOv';
const THEO_PRAYING = 'media/characters/theo-praying.png';
const PRAYING_FALLBACK = '🙏';
let artAvail = false;
let artProbed = false;

/** Same graceful probe as the other Theo art slots: the emoji stands in until
    Eric's praying-Theo lands, then it swaps with zero code edits. */
function probeArt(): void {
  if (artProbed) return;
  artProbed = true;
  try {
    const img = new Image();
    img.onload = () => {
      artAvail = true;
      fillArt();
    };
    img.onerror = () => {
      /* not built yet — 🙏 stays */
    };
    img.src = THEO_PRAYING;
  } catch {
    /* no Image() (tests) — 🙏 stays */
  }
}

function fillArt(): void {
  const slot = document.getElementById('pbArt');
  if (!slot) return;
  slot.innerHTML = artAvail
    ? `<img class="pb-art-img" src="${THEO_PRAYING}" alt="" />`
    : `<span class="pb-art-emoji">${PRAYING_FALLBACK}</span>`;
}

/** Minimal HTML escape (local, so this module stays a leaf like roundPicker). */
function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** The ring's circumference for r=34 — shared by the markup and the CSS var. */
const RING_R = 34;
const RING_C = 2 * Math.PI * RING_R;

/**
 * Show the moment and resolve when the player continues. Safe with no DOM
 * (resolves immediately). NOTHING here awards, tracks a streak, or plays a
 * game sound — see the reverence policy above.
 */
export function showPrayerBreak(content: PrayerBreakContent): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();

  const root = document.createElement('div');
  root.id = OVERLAY_ID;
  root.className = 'pb-ov';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', content.title);

  const reduced = prefersReducedMotion();
  const hold = Math.min(MAX_HOLD_SECONDS, Math.max(MIN_HOLD_SECONDS, Math.round(content.holdSeconds) || DEFAULT_HOLD_SECONDS));
  root.style.setProperty('--pb-hold', hold + 's');
  root.style.setProperty('--pb-ring', RING_C.toFixed(2));

  root.innerHTML =
    `<div class="pb-card">` +
    `<div class="pb-title">${escHtml(content.title)}</div>` +
    `<div class="pb-art" id="pbArt" aria-hidden="true"><span class="pb-art-emoji">${PRAYING_FALLBACK}</span></div>` +
    `<div class="pb-line">${escHtml(content.line)}</div>` +
    (content.attribution ? `<div class="pb-attrib">${escHtml(content.attribution)}</div>` : '') +
    (content.fullText ? `<div class="pb-full">${escHtml(content.fullText)}</div>` : '') +
    `<div class="pb-ringwrap" aria-hidden="true">` +
    `<svg class="pb-ring${reduced ? ' still' : ''}" viewBox="0 0 80 80">` +
    `<circle class="pb-ring-track" cx="40" cy="40" r="${RING_R}"></circle>` +
    `<circle class="pb-ring-run" cx="40" cy="40" r="${RING_R}"></circle>` +
    `</svg>` +
    `</div>` +
    `<button type="button" class="btn pb-amen" id="pbAmen">Amen. Continue</button>` +
    `</div>`;

  const prev = document.getElementById(OVERLAY_ID);
  if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
  const host = (document.querySelector('.screen') as HTMLElement | null) || document.body;
  host.appendChild(root);
  fillArt();
  probeArt();

  return new Promise<void>((resolve) => {
    let settled = false;

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        finish();
      }
    }

    function finish(): void {
      if (settled) return;
      settled = true;
      clearTimeout(ringTimer);
      document.removeEventListener('keydown', onKey, true);
      root.classList.add('mo-out');
      const remove = (): void => {
        if (root.parentNode) root.parentNode.removeChild(root);
      };
      if (reduced) remove();
      else setTimeout(remove, 260);
      resolve();
    }

    // The ring is encouragement only: when it completes the button simply gets a
    // gentle "ready" accent. It never gated the button in the first place.
    const ringTimer = setTimeout(() => root.classList.add('pb-held'), hold * 1000);

    const amen = root.querySelector<HTMLButtonElement>('#pbAmen');
    if (amen) {
      amen.disabled = false; // stated explicitly: the ring NEVER locks this
      amen.addEventListener('click', finish);
      try {
        amen.focus();
      } catch {
        /* focus can throw in odd embeds — tapping still works */
      }
    }
    document.addEventListener('keydown', onKey, true);
  });
}

/** Load the content and show the moment — the one call gameController makes. */
export async function runPrayerBreak(): Promise<void> {
  const content = await loadPrayerBreak();
  await showPrayerBreak(content);
}
